// src/components/dashboard/groups/GroupSettingsModal.tsx
// ✅ Modal de Configurações: Compacto para membros, completo para admins
// ✅ CSS ISOLADO com prefixo confgpp-

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { StudyGroupDetailed } from '../GroupTypes';
import {
    X, Copy, LogOut, Edit, Save, Image, Trash2, AlertTriangle, Crown, UserMinus, Users
} from 'lucide-react';
import './GroupSettingsModal.css';

interface GroupSettingsModalProps {
    isOpen: boolean;
    group: StudyGroupDetailed;
    isAdmin: boolean;
    onClose: () => void;
    onUpdate: () => void;
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const API_BASE_URL = '/api';

const GroupSettingsModal: React.FC<GroupSettingsModalProps> = ({
    isOpen,
    group,
    isAdmin,
    onClose,
    onUpdate,
    onShowToast
}) => {
    const navigate = useNavigate();

    const [isEditingName, setIsEditingName] = useState(false);
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [newName, setNewName] = useState(group.name);
    const [newDescription, setNewDescription] = useState(group.description || '');
    const [groupIcon, setGroupIcon] = useState<File | null>(null);
    const [iconPreview, setIconPreview] = useState<string | null>(null);
    const [isUploadingIcon, setIsUploadingIcon] = useState(false);

    const [confirmAction, setConfirmAction] = useState<{
        type: 'leave' | 'delete' | 'promote' | 'remove' | null;
        targetUserId?: string;
        targetUsername?: string;
    }>({ type: null });

    const [showMemberManagement, setShowMemberManagement] = useState(false);
    const [members, setMembers] = useState<any[]>([]);

    const [deletionStatus, setDeletionStatus] = useState<{
        isActive: boolean;
        approvals: number;
        required: number;
    } | null>(null);

    // ✅ Envolva com useCallback
    const checkDeletionStatus = useCallback(() => {
        if (group.deletionRequest?.isActive) {
            setDeletionStatus({
                isActive: true,
                approvals: group.deletionRequest.approvals?.length || 0,
                required: group.members.filter((m: any) => m.role === 'admin').length
            });
        } else {
            setDeletionStatus(null);
        }
    }, [group]); // ✅ Depende de group

    // ✅ Envolva com useCallback
    const loadMembers = useCallback(async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/groups/${group._id}/ranking`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setMembers(data.ranking);
            }
        } catch (error) {
            console.error('Erro ao carregar membros:', error);
        }
    }, [group._id]); // ✅ Depende de group._id

    useEffect(() => {
        if (isOpen && isAdmin) {
            loadMembers();
            checkDeletionStatus();
        }
    }, [isOpen, isAdmin, loadMembers, checkDeletionStatus]);

    const handleCopyInviteCode = () => {
        navigator.clipboard.writeText(group.inviteCode);
        onShowToast('Código copiado', 'success');
    };

    const handleLeaveGroup = () => {
        const adminCount = group.members.filter(m => m.role === 'admin').length;

        if (isAdmin && adminCount === 1) {
            onShowToast('Você é o único administrador. Promova outro membro antes de sair.', 'error');
            return;
        }

        setConfirmAction({ type: 'leave' });
    };

    const confirmLeaveGroup = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/groups/${group._id}/leave`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                onShowToast('Você saiu do grupo', 'success');
                onClose();
                setTimeout(() => navigate('/dashboard'), 1500);
            } else {
                const data = await response.json();
                onShowToast(data.message || 'Erro ao sair do grupo', 'error');
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao sair do grupo', 'error');
        } finally {
            setConfirmAction({ type: null });
        }
    };

    const handleUpdateName = async () => {
        if (!newName.trim()) {
            onShowToast('Nome não pode estar vazio', 'error');
            return;
        }

        if (newName.trim() === group.name) {
            setIsEditingName(false);
            return;
        }

        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/groups/${group._id}/update`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ name: newName.trim() })
            });

            if (response.ok) {
                onShowToast('Nome atualizado', 'success');
                setIsEditingName(false);
                onUpdate();
            } else {
                const data = await response.json();
                onShowToast(data.message || 'Erro ao atualizar', 'error');
            }
        } catch (error) {
            onShowToast('Erro ao atualizar nome', 'error');
        }
    };

    const handleUpdateDescription = async () => {
        if (newDescription.trim() === (group.description || '')) {
            setIsEditingDescription(false);
            return;
        }

        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/groups/${group._id}/update`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ description: newDescription.trim() })
            });

            if (response.ok) {
                onShowToast('Descrição atualizada', 'success');
                setIsEditingDescription(false);
                onUpdate();
            } else {
                const data = await response.json();
                onShowToast(data.message || 'Erro ao atualizar', 'error');
            }
        } catch (error) {
            onShowToast('Erro ao atualizar descrição', 'error');
        }
    };

    const handleIconUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                onShowToast('Imagem muito grande. Máximo 5MB', 'error');
                return;
            }
            if (!file.type.startsWith('image/')) {
                onShowToast('Apenas arquivos de imagem', 'error');
                return;
            }
            setGroupIcon(file);
            setIconPreview(URL.createObjectURL(file));
        }
    };

    const handleUploadIcon = async () => {
        if (!groupIcon) {
            onShowToast('Selecione uma imagem primeiro', 'error');
            return;
        }

        setIsUploadingIcon(true);
        try {
            const token = localStorage.getItem('accessToken');
            const formData = new FormData();
            formData.append('icon', groupIcon);

            const response = await fetch(`${API_BASE_URL}/groups/${group._id}/upload-icon`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            });

            if (response.ok) {
                onShowToast('Ícone atualizado', 'success');
                setGroupIcon(null);
                setIconPreview(null);
                onUpdate();
            } else {
                const data = await response.json();
                onShowToast(data.message || 'Erro ao fazer upload', 'error');
            }
        } catch (error) {
            onShowToast('Erro ao fazer upload do ícone', 'error');
        } finally {
            setIsUploadingIcon(false);
        }
    };

    const handleDeleteGroup = () => {
        setConfirmAction({ type: 'delete' });
    };

    const confirmDeleteGroup = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/groups/${group._id}/request-deletion`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();

                if (data.deleted) {
                    onShowToast('Grupo excluído', 'success');
                    onClose();
                    setTimeout(() => navigate('/dashboard'), 1500);
                } else {
                    setDeletionStatus({
                        isActive: true,
                        approvals: data.approvals,
                        required: data.requiredApprovals
                    });
                    onShowToast(data.message, 'info');
                    onUpdate();
                }
            } else {
                const data = await response.json();
                onShowToast(data.message || 'Erro ao excluir', 'error');
            }
        } catch (error) {
            onShowToast('Erro ao excluir grupo', 'error');
        } finally {
            setConfirmAction({ type: null });
        }
    };

    const handleApproveDeletion = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/groups/${group._id}/approve-deletion`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();

                if (data.deleted) {
                    onShowToast('Grupo excluído', 'success');
                    onClose();
                    setTimeout(() => navigate('/dashboard'), 1500);
                } else {
                    setDeletionStatus({
                        isActive: true,
                        approvals: data.approvals,
                        required: data.requiredApprovals
                    });
                    onShowToast('Aprovação registrada', 'success');
                    onUpdate();
                }
            } else {
                const data = await response.json();
                onShowToast(data.message || 'Erro ao aprovar', 'error');
            }
        } catch (error) {
            onShowToast('Erro ao aprovar exclusão', 'error');
        }
    };

    const handlePromoteMember = (userId: string, username: string) => {
        setConfirmAction({ type: 'promote', targetUserId: userId, targetUsername: username });
    };

    const confirmPromoteMember = async () => {
        if (!confirmAction.targetUserId) return;

        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/groups/${group._id}/promote/${confirmAction.targetUserId}`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                onShowToast('Membro promovido a administrador', 'success');
                loadMembers();
                onUpdate();
            } else {
                const data = await response.json();
                onShowToast(data.message || 'Erro ao promover', 'error');
            }
        } catch (error) {
            onShowToast('Erro ao promover membro', 'error');
        } finally {
            setConfirmAction({ type: null });
        }
    };

    const handleRemoveMember = (userId: string, username: string) => {
        setConfirmAction({ type: 'remove', targetUserId: userId, targetUsername: username });
    };

    const confirmRemoveMember = async () => {
        if (!confirmAction.targetUserId) return;

        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/groups/${group._id}/remove-member/${confirmAction.targetUserId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                onShowToast('Membro removido', 'success');
                loadMembers();
                onUpdate();
            } else {
                const data = await response.json();
                onShowToast(data.message || 'Erro ao remover', 'error');
            }
        } catch (error) {
            onShowToast('Erro ao remover membro', 'error');
        } finally {
            setConfirmAction({ type: null });
        }
    };

    if (!isOpen) return null;

    const renderConfirmation = () => {
        if (!confirmAction.type) return null;

        const confirmations = {
            leave: {
                title: 'Sair do Grupo',
                message: 'Tem certeza que deseja sair deste grupo?',
                onConfirm: confirmLeaveGroup,
                danger: true
            },
            delete: {
                title: 'Excluir Grupo',
                message: 'Tem certeza que deseja excluir este grupo? Esta ação não pode ser desfeita.',
                onConfirm: confirmDeleteGroup,
                danger: true
            },
            promote: {
                title: 'Promover Administrador',
                message: `Deseja promover ${confirmAction.targetUsername} a administrador do grupo?`,
                onConfirm: confirmPromoteMember,
                danger: false
            },
            remove: {
                title: 'Expulsar Membro',
                message: `Tem certeza que deseja expulsar ${confirmAction.targetUsername} do grupo?`,
                onConfirm: confirmRemoveMember,
                danger: true
            }
        };

        const config = confirmations[confirmAction.type];

        return (
            <div className="confgpp-inline-confirmation">
                <div className={`confgpp-confirmation-box ${config.danger ? 'confgpp-danger' : ''}`}>
                    <AlertTriangle size={20} className="confgpp-confirmation-icon" />
                    <div className="confgpp-confirmation-content">
                        <h4>{config.title}</h4>
                        <p>{config.message}</p>
                    </div>
                    <div className="confgpp-confirmation-actions">
                        <button className="confgpp-btn-confirm" onClick={config.onConfirm}>
                            Confirmar
                        </button>
                        <button className="confgpp-btn-cancel-confirm" onClick={() => setConfirmAction({ type: null })}>
                            Cancelar
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const hasUserApprovedDeletion = (): boolean => {
        if (!group?.deletionRequest?.isActive) return false;

        const currentUserId = localStorage.getItem('userId');

        return group.deletionRequest.approvals.some(
            (approval: any) => {
                const approvalUserId = typeof approval.userId === 'string'
                    ? approval.userId
                    : (approval.userId as any)._id || approval.userId;
                return approvalUserId === currentUserId;
            }
        );
    };

    return (
        <>
            <div className="confgpp-overlay" onClick={onClose}>
                <div
                    className={`confgpp-modal ${isAdmin ? 'confgpp-admin' : 'confgpp-member'}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="confgpp-header">
                        <h3>Configurações do Grupo</h3>
                        <button className="confgpp-btn-close" onClick={onClose}>
                            <X size={20} />
                        </button>
                    </div>

                    {/* Conteúdo */}
                    <div className="confgpp-content">
                        {/* Código de Convite */}
                        <div className="confgpp-section">
                            <label className="confgpp-label">Código de Convite</label>
                            <div className="confgpp-invite-row">
                                <div className="confgpp-invite-display">{group.inviteCode}</div>
                                <button className="confgpp-btn-copy" onClick={handleCopyInviteCode}>
                                    <Copy size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Edição (apenas admin) */}
                        {isAdmin && (
                            <>
                                {/* Nome */}
                                <div className="confgpp-section">
                                    <label className="confgpp-label">Nome do Grupo</label>
                                    {isEditingName ? (
                                        <div className="confgpp-edit-row">
                                            <input
                                                type="text"
                                                value={newName}
                                                onChange={(e) => setNewName(e.target.value)}
                                                maxLength={100}
                                                autoFocus
                                            />
                                            <button className="confgpp-btn-icon confgpp-success" onClick={handleUpdateName}>
                                                <Save size={16} />
                                            </button>
                                            <button className="confgpp-btn-icon" onClick={() => {
                                                setIsEditingName(false);
                                                setNewName(group.name);
                                            }}>
                                                <X size={16} />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="confgpp-display-row">
                                            <span>{group.name}</span>
                                            <button className="confgpp-btn-icon" onClick={() => setIsEditingName(true)}>
                                                <Edit size={16} />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Descrição */}
                                <div className="confgpp-section">
                                    <label className="confgpp-label">Descrição</label>
                                    {isEditingDescription ? (
                                        <div className="confgpp-edit-row">
                                            <textarea
                                                value={newDescription}
                                                onChange={(e) => setNewDescription(e.target.value)}
                                                maxLength={500}
                                                rows={2}
                                                autoFocus
                                            />
                                            <button className="confgpp-btn-icon confgpp-success" onClick={handleUpdateDescription}>
                                                <Save size={16} />
                                            </button>
                                            <button className="confgpp-btn-icon" onClick={() => {
                                                setIsEditingDescription(false);
                                                setNewDescription(group.description || '');
                                            }}>
                                                <X size={16} />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="confgpp-display-row">
                                            <span>{group.description || 'Sem descrição'}</span>
                                            <button className="confgpp-btn-icon" onClick={() => setIsEditingDescription(true)}>
                                                <Edit size={16} />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Ícone */}
                                <div className="confgpp-section">
                                    <label className="confgpp-label">Ícone do Grupo</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleIconUpload}
                                        style={{ display: 'none' }}
                                        id="icon-upload-modal"
                                    />
                                    <label htmlFor="icon-upload-modal" className="confgpp-btn-select-file">
                                        <Image size={16} />
                                        <span>Selecionar Imagem</span>
                                    </label>

                                    {iconPreview && (
                                        <div className="confgpp-icon-preview">
                                            <img src={iconPreview} alt="Preview" />
                                            <div className="confgpp-icon-actions">
                                                <button className="confgpp-btn-small confgpp-success" onClick={handleUploadIcon} disabled={isUploadingIcon}>
                                                    {isUploadingIcon ? 'Enviando...' : 'Salvar'}
                                                </button>
                                                <button className="confgpp-btn-small" onClick={() => {
                                                    setGroupIcon(null);
                                                    setIconPreview(null);
                                                }}>
                                                    Cancelar
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Gerenciamento de Membros */}
                                <div className="confgpp-section">
                                    <label className="confgpp-label">Gerenciar Membros</label>
                                    <button
                                        className="confgpp-btn-toggle-members"
                                        onClick={() => setShowMemberManagement(!showMemberManagement)}
                                    >
                                        <Users size={16} />
                                        <span>{showMemberManagement ? 'Ocultar' : 'Mostrar'} Membros</span>
                                    </button>

                                    {showMemberManagement && (
                                        <div className="confgpp-members-list">
                                            {members.map((member) => (
                                                <div key={member.userId} className="confgpp-member-item">
                                                    <div className="confgpp-member-info">
                                                        {member.profilePhoto ? (
                                                            <img
                                                                src={member.profilePhoto || ''}
                                                                alt={member.username}
                                                                className="confgpp-member-photo"
                                                            />
                                                        ) : (
                                                            <div className="confgpp-member-placeholder">
                                                                {member.username.charAt(0).toUpperCase()}
                                                            </div>
                                                        )}
                                                        <div>
                                                            <span className="confgpp-member-name">{member.username}</span>
                                                            {member.role === 'admin' && (
                                                                <span className="confgpp-admin-tag">Admin</span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {member.userId !== localStorage.getItem('userId') && member.role === 'member' && (
                                                        <div className="confgpp-member-actions">
                                                            <button
                                                                className="confgpp-btn-icon-small confgpp-success"
                                                                onClick={() => handlePromoteMember(member.userId, member.username)}
                                                                title="Promover a admin"
                                                            >
                                                                <Crown size={14} />
                                                            </button>
                                                            <button
                                                                className="confgpp-btn-icon-small confgpp-danger"
                                                                onClick={() => handleRemoveMember(member.userId, member.username)}
                                                                title="Expulsar"
                                                            >
                                                                <UserMinus size={14} />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Status de Exclusão Pendente */}
                                {deletionStatus?.isActive && (
                                    <div className="confgpp-section">
                                        <div className="confgpp-deletion-alert">
                                            <AlertTriangle size={20} />
                                            <div>
                                                <h4>Exclusão Pendente</h4>
                                                <p>Aprovações: <strong>{deletionStatus.approvals}</strong> de <strong>{deletionStatus.required}</strong></p>
                                                <p className="confgpp-deletion-note">Todos os administradores devem aprovar</p>
                                            </div>
                                            {!hasUserApprovedDeletion() && (
                                                <button
                                                    className="confgpp-btn-approve-deletion"
                                                    onClick={handleApproveDeletion}
                                                >
                                                    Aprovar Exclusão
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Ações */}
                        <div className="confgpp-actions">
                            <button className="confgpp-btn-leave" onClick={handleLeaveGroup}>
                                <LogOut size={16} />
                                <span>Sair do Grupo</span>
                            </button>

                            {isAdmin && !deletionStatus?.isActive && (
                                <button className="confgpp-btn-delete" onClick={handleDeleteGroup}>
                                    <Trash2 size={16} />
                                    <span>Excluir Grupo</span>
                                </button>
                            )}
                        </div>

                        {/* Confirmação Inline */}
                        {renderConfirmation()}
                    </div>
                </div>
            </div>
        </>
    );
};

export default GroupSettingsModal;